import Service, { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import ENV from 'tio-employee/config/environment';
import { resolveDemoScenario } from 'tio-employee/demo/registry';
import { USER_ID as DEMO_USER_ID } from 'tio-employee/demo/identity-fixtures';
import type { FetchMatcher } from 'tio-data/demo/types';
import type FeaturesService from 'tio-common/services/features';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  DEMO_CLIENT_LIST,
  DEMO_PRODUCT_LIST,
  DEMO_SESSION_ROLE_LIST,
  DEMO_TA_PAYMENT_MODEL_LIST,
  DEMO_TAS_SCENARIO_OPTION_LIST,
  DEMO_DEBT_LEVEL_LIST,
  DEMO_USER_SCENARIO_LIST,
  applyDemoSession,
  exitDemoSession,
  isDemoSessionArmed,
  resolveDemoEntryHref,
  getCurrentDemoClientId,
  getCurrentDemoProductIds,
  getCurrentDemoRoleId,
  getCurrentDemoTaPaymentModelId,
  getCurrentDemoDebtLevelId,
  getCurrentDemoUserScenarioId,
  getCurrentDemoPslfScenarioId,
  isFeatureEnabled,
  getDemoData,
  getDemoProduct,
  getDemoSessionRole,
  getDemoTaPaymentModel,
  getDemoTasScenarioOption,
  getDemoDebtLevel,
  getDemoUserScenario,
  isReimaginedDemoMode,
  isPslfEligibleClient,
  selectionHasTuitionAssistance,
  type DemoClientId,
  type DemoProductId,
  type DemoSessionRoleId,
  type DemoSessionSelection,
  type DemoSessionSelectionSnapshot,
  type DemoTaPaymentModelId,
  type DemoTasScenarioId,
  type DemoDebtLevelId,
  type DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Relationship types treated as internal TIO staff for the (currently
 * disabled) production gate. The precise definition is intentionally
 * conservative — superuser + TIO approver — and will be finalised when
 * `demoModeRequireStaffRoleInProd` is turned on.
 */
const STAFF_RELATIONSHIP_TYPES = new Set<string>([
  'TAS.Admin.SUPERUSER',
  'TAS.Approver.TIO_APPROVER',
]);

/**
 * The capability keys demo "owns" — the active scenario's product/feature flags
 * (career-pathways `DemoScenario.features`). The demo capability source speaks
 * only for these (and only while a session is active); it abstains on everything
 * else — including `demoMode` itself, which {@link DemoModeService.isAvailable}
 * reads, so that key keeps resolving from the static flag tier (no re-entrancy).
 */
const DEMO_SCENARIO_CAPABILITY_KEYS = new Set<string>([
  'tuitionAssistance',
  'studentLoanRepayment',
  'careerPathways',
  'financialWellness',
  'pslf',
  'employerMatch',
]);

/**
 * The single chokepoint for demo mode.
 *
 * It owns the availability gate (environment + role) and mediates every read /
 * write of demo-session state, so routes, components, the hotkey modal — and,
 * in a later phase, the request-manager demo handler — never sniff
 * localStorage / URL params or the environment directly.
 *
 * Phase 0 delegates the underlying session state to the existing
 * `career-pathways-demo-data` helpers; later phases move that state behind a
 * typed scenario registry and a namespaced storage scheme without changing
 * this public surface.
 */
export default class DemoModeService extends Service {
  @service declare features: FeaturesService;
  @service declare sessionContext: SessionContextService;

  #originalFetch?: typeof fetch;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.#installFetchInterceptor();
  }

  /**
   * Installs a global `fetch` interceptor (seam B) so that raw-fetch paths that
   * bypass the store / request manager — service methods (e.g. limits status)
   * and custom adapter methods (e.g. tas-participant eligibility) — also return
   * demo fixtures while a demo session is active. It is a transparent
   * pass-through whenever demo mode is inactive, and is restored when the
   * service is torn down.
   *
   * While a demo session IS active, this seam is also the network-free
   * guarantee: an unmatched request to the live API origin is served an inert
   * response instead of reaching the network (see {@link blockedApiRequests}).
   * Every HTTP path in the app terminates in `window.fetch` (WarpDrive's
   * terminal Fetch handler, the raw download helpers, telemetry — there is no
   * XHR/sendBeacon usage), so this one gate covers them all; WebSockets are
   * separately disabled via `cable.disabled` in the authenticated route.
   */
  #installFetchInterceptor(): void {
    if (typeof window === 'undefined' || this.#originalFetch) {
      return;
    }
    const original = window.fetch.bind(window);
    this.#originalFetch = original;

    const intercept: typeof fetch = (input, init) => {
      try {
        const demoResponse = this.#demoResponseFor(input, init);
        if (demoResponse) {
          return Promise.resolve(demoResponse);
        }
      } catch {
        // Never let demo interception break a real request.
      }
      return original(input, init);
    };

    window.fetch = intercept;
    registerDestructor(this, () => {
      if (this.#originalFetch && window.fetch === intercept) {
        window.fetch = this.#originalFetch;
      }
    });
  }

  #demoResponseFor(input: RequestInfo | URL, init?: RequestInit): Response | undefined {
    let url: string;
    let method: string | undefined;
    if (typeof input === 'string') {
      url = input;
      method = init?.method;
    } else if (input instanceof URL) {
      url = input.href;
      method = init?.method;
    } else {
      url = input.url;
      method = init?.method ?? input.method;
    }

    const verb = (method ?? 'GET').toUpperCase();
    // The match attempt gets its own guard: an exception inside scenario
    // resolution / fixture building must NOT disable the network-free block
    // below (the interceptor's outer catch used to silently turn any such
    // exception into full passthrough — every fixture AND the guarantee
    // vanished at once, with nothing in the console).
    let match: FetchMatcher | undefined;
    try {
      match = this.#demoFetchMatch(url, verb);
    } catch (error) {
      this.#warnResolveFailure(error);
      match = undefined;
    }
    if (match) {
      const body = typeof match.json === 'function' ? match.json(url) : match.json;
      return new Response(JSON.stringify(body), {
        status: match.status ?? 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    return this.#blockedApiResponse(url, verb);
  }

  #warnedResolveFailures = new Set<string>();

  /** A broken scenario resolve is a demo-breaking bug — say so, loudly, once per message. */
  #warnResolveFailure(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    if (this.#warnedResolveFailures.has(message)) {
      return;
    }
    this.#warnedResolveFailures.add(message);
    console.error(
      `[demo] scenario resolution threw — fixtures are NOT being served (demo is broken): ${message}`,
      error
    );
  }

  /**
   * The live API origin the network-free guarantee protects. `null` when the
   * environment defines no resolvable apiHost (the test environment uses ''
   * so MSW can intercept relative URLs) — with no API origin the block is
   * inert and the interceptor is a pure fixture layer. Overridable so tests
   * can point the guarantee at a sentinel origin.
   */
  get apiOrigin(): string | null {
    try {
      return new URL(ENV.apiHost).origin;
    } catch {
      return null;
    }
  }

  /**
   * Requests to the live API origin that were served an inert response while
   * a demo session was active, because no fetch-matcher (and no upstream
   * request-handler fixture) claimed them. Each one is a FIXTURE GAP, not an
   * error: the demo stayed network-free, and the worst case is an empty
   * widget. The demo-coverage audit reads this (via
   * `window.__demoBlockedRequests`) to report gaps per route.
   */
  get blockedApiRequests(): ReadonlyArray<{ method: string; url: string }> {
    return this.#blockedApiRequests;
  }

  #blockedApiRequests: Array<{ method: string; url: string }> = [];
  #warnedBlockedEndpoints = new Set<string>();

  /**
   * The network-free guarantee (the audit finding that motivated it: the
   * demo seams were passthrough-by-design for unmatched requests, so an
   * unscripted page silently mixed LIVE API data into a demo and made demos
   * depend on API availability mid-pitch). While a demo session is active,
   * an unmatched request to the live API origin never reaches the network:
   *
   * - reads (GET/HEAD) resolve to an empty JSON:API collection — the most
   *   benign generally-valid shape. A surface that renders wrongly on it is
   *   a fixture gap; author a fixture (the demo audit surfaces these),
   *   never relax the block.
   * - everything else (mutations, telemetry posts) is acknowledged with an
   *   inert 204 and nothing persists — fire-and-forget callers (telemetry,
   *   sign-out) complete normally.
   *
   * Non-API origins (app assets, vite/HMR, analytics, Zendesk, S3 signed
   * URLs) pass through untouched, and inactive sessions are untouched
   * entirely — real users always reach the live API.
   */
  #blockedApiResponse(url: string, verb: string): Response | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const apiOrigin = this.apiOrigin;
    if (!apiOrigin) {
      return undefined;
    }
    try {
      if (new URL(url, window.location.origin).origin !== apiOrigin) {
        return undefined;
      }
    } catch {
      return undefined;
    }

    this.#recordBlockedRequest(verb, url);
    if (verb === 'GET' || verb === 'HEAD') {
      // HEURISTIC over this API's conventional URL shape (JSON:API itself
      // prescribes no URL structure): an ODD number of path segments is
      // treated as a collection (`/employees`, `/users/1/summarize-loans`),
      // an EVEN number as a single resource (`/employees/1`). Good enough to
      // keep blocked pages rendering; a page that needs better gets a real
      // fixture. Single-resource reads must get `data: null`
      // — an empty ARRAY materializes as "[] is not a ReactiveResource or
      // Model known to WarpDrive" and error-pages the route (seen live on
      // /pslf/dashboard and /admin/employees/add), where `data: null` resolves
      // to a coherent empty record read.
      let segments: number;
      try {
        segments = new URL(url, window.location.origin).pathname.split('/').filter(Boolean).length;
      } catch {
        segments = 1;
      }
      const data = segments % 2 === 0 ? null : [];
      return new Response(JSON.stringify({ data, meta: { demoBlocked: true } }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    return new Response(null, { status: 204 });
  }

  /**
   * Records a blocked request for the audit (window mirror) and warns once
   * per distinct endpoint so a fixture gap is visible in the console without
   * a polling call flooding it.
   */
  #recordBlockedRequest(method: string, url: string): void {
    const entry = { method, url };
    this.#blockedApiRequests.push(entry);
    const mirror = window as Window & { __demoBlockedRequests?: Array<typeof entry> };
    (mirror.__demoBlockedRequests ??= []).push(entry);

    const endpoint = `${method} ${url.split('?')[0] ?? url}`;
    if (!this.#warnedBlockedEndpoints.has(endpoint)) {
      this.#warnedBlockedEndpoints.add(endpoint);
      console.warn(`[demo] network-free: blocked unmatched API request (fixture gap?) ${endpoint}`);
    }
  }

  /**
   * Finds the active scenario's fetch-matcher for a raw `url`+`method` while a
   * demo session is active, else `undefined`. The single matching point shared
   * by the fetch interceptor (which also honours the matcher's `status`) and
   * `demoJsonFor` (the unit-test probe of this seam).
   */
  #demoFetchMatch(url: string, method: string): FetchMatcher | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const verb = method.toUpperCase();
    const resolved = resolveDemoScenario(this.currentSelection);
    return resolved?.fetchMatchers?.find((fetchMatcher) => {
      if (fetchMatcher.method && fetchMatcher.method.toUpperCase() !== verb) {
        return false;
      }
      if (fetchMatcher.urlMatches) {
        return fetchMatcher.urlMatches(url);
      }
      return fetchMatcher.urlIncludes ? url.includes(fetchMatcher.urlIncludes) : false;
    });
  }

  /**
   * Returns the demo response body for a raw `url`+`method` while a demo session
   * is active (matched against the active scenario's fetch-matchers), or the
   * `undefined` sentinel when there is no active demo match. The body is `unknown`
   * because it mirrors arbitrary backend JSON; `FetchMatcher.json` is always
   * defined, so `undefined` unambiguously means "no match".
   *
   * No production code calls this anymore: the WarpDrive request migration
   * removed the custom adapters whose raw `ajax` used to consult it, so every
   * HTTP path now reaches the fetch interceptor above (or the request-manager
   * demo handler). Retained as the direct unit-test probe of the fetch-matcher
   * seam (tests/unit/services/demo-mode-test.ts and the fixture tests) — if it
   * is ever removed, those tests must drive the interceptor instead.
   */
  demoJsonFor(url: string, method = 'GET'): unknown {
    const match = this.#demoFetchMatch(url, method);
    if (!match) {
      return undefined;
    }
    return typeof match.json === 'function' ? match.json(url) : match.json;
  }

  /**
   * Expose demo state as a {@link CapabilitySource} for the features service
   * (highest rank). While a demo session is active it answers the active
   * scenario's product/feature flags; otherwise — and for any non-scenario key —
   * it abstains, so `features.can(...)` falls through to beta/flag/default.
   */
  asCapabilitySource(): CapabilitySource {
    return {
      id: 'demo',
      rank: CAPABILITY_SOURCE_RANK.demo,
      resolve: (key) => {
        if (!this.isActive || !DEMO_SCENARIO_CAPABILITY_KEYS.has(key as string)) {
          return undefined;
        }
        return isFeatureEnabled(key as Parameters<typeof isFeatureEnabled>[0]);
      },
    };
  }

  /**
   * Whether the demo system is reachable in this session. Fails safe to
   * `false` when the master flag is absent. Enabled in every environment
   * today; flip `featureFlags.demoModeRequireStaffRoleInProd` to restrict
   * production to internal staff.
   */
  get isAvailable(): boolean {
    try {
      if (!this.features.isEnabled('demoMode')) {
        return false;
      }
      if (this.features.tioEnvironment !== 'production') {
        return true;
      }
      if (!this.features.isEnabled('demoModeRequireStaffRoleInProd')) {
        return true;
      }
      return this.isStaffUser;
    } catch {
      // `features.isEnabled` throws if the features service has not been seeded
      // with ENV yet. Because this getter gates the root application template,
      // fail closed (demo unavailable) rather than crash the app.
      return false;
    }
  }

  /** True when a demo session is currently applied (and demo mode is allowed). */
  get isActive(): boolean {
    return this.isAvailable && isReimaginedDemoMode();
  }

  /**
   * True when a sticky demo session is armed, even if the CURRENT route does
   * not render demo data (see {@link isActive} — the sticky flag is
   * path-scoped). `isArmed && !isActive` is the "paused on a non-demoable
   * route, showing real data" state the demo pill warns about.
   */
  get isArmed(): boolean {
    return this.isAvailable && isDemoSessionArmed();
  }

  /**
   * Exit the demo session entirely: wipe all sticky demo state and reload the
   * current route with every demo param stripped. The single exit path used by
   * the demo pill, the picker footer, and the reimagined header toggle.
   */
  exit(): void {
    exitDemoSession();
  }

  /**
   * From a non-demoable route with an armed (paused) session, jump back into
   * the demo — the sticky flag re-activates on arrival. Lands on the CURRENT
   * shipped app by role (participants → the dashboard, other roles → the
   * admin experience), mirroring the app's own role redirect; the reimagined
   * developer sandbox is not a sales-demo destination (#8022).
   */
  resume(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.location.assign(resolveDemoEntryHref(window.location.href, getCurrentDemoRoleId()));
  }

  #pickerOpener?: () => void;

  /**
   * The globally-rendered picker (DemoDataShortcut) registers its modal opener
   * here for its lifetime, so any surface with this service — e.g. the
   * reimagined header's "Demo settings" launcher — can open THE one picker
   * instead of growing its own control surface. Returns the unregister
   * cleanup; registration is last-writer-wins (the picker renders once).
   */
  registerPickerOpener(open: () => void): () => void {
    this.#pickerOpener = open;
    return () => {
      if (this.#pickerOpener === open) {
        this.#pickerOpener = undefined;
      }
    };
  }

  /** Open the demo picker modal. A no-op when the picker is not rendered. */
  openPicker(): void {
    this.#pickerOpener?.();
  }

  /**
   * True only when THIS session IS the fabricated demo identity — the `/me`
   * sentinel ({@link DEMO_USER_ID}) that has no live backend record. Distinct from
   * {@link isActive}: the sticky demo flag is path-scoped, so `isActive` can be true
   * for a REAL logged-in user (real `/me`) on a demo-flagged path — that user must
   * still reach the live API. Gate "would this hit the API with a non-live id?"
   * decisions on this, NOT on `isActive`.
   */
  get isDemoSessionUser(): boolean {
    return this.sessionContext.user?.id === DEMO_USER_ID;
  }

  /** Whether the current role is internal TIO staff (used by the prod gate). */
  get isStaffUser(): boolean {
    const relationshipType = this.sessionContext.currentRole?.relationshipType;
    return relationshipType ? STAFF_RELATIONSHIP_TYPES.has(relationshipType) : false;
  }

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get products() {
    return DEMO_PRODUCT_LIST;
  }

  get roles() {
    return DEMO_SESSION_ROLE_LIST;
  }

  get userScenarios() {
    return DEMO_USER_SCENARIO_LIST;
  }

  get taPaymentModels() {
    return DEMO_TA_PAYMENT_MODEL_LIST;
  }

  get tasScenarios() {
    return DEMO_TAS_SCENARIO_OPTION_LIST;
  }

  get debtLevels() {
    return DEMO_DEBT_LEVEL_LIST;
  }

  get currentSelection(): DemoSessionSelectionSnapshot {
    return {
      clientId: getCurrentDemoClientId(),
      productIds: getCurrentDemoProductIds(),
      roleId: getCurrentDemoRoleId(),
      userScenarioId: getCurrentDemoUserScenarioId(),
      taPaymentModelId: getCurrentDemoTaPaymentModelId(),
      pslfScenarioId: getCurrentDemoPslfScenarioId(),
      debtLevelId: getCurrentDemoDebtLevelId(),
    };
  }

  clientName(clientId: DemoClientId): string {
    return getDemoData(clientId).client.name;
  }

  productName(productId: DemoProductId): string {
    return getDemoProduct(productId).name;
  }

  roleName(roleId: DemoSessionRoleId): string {
    return getDemoSessionRole(roleId).name;
  }

  userScenarioName(userScenarioId: DemoUserScenarioId): string {
    return getDemoUserScenario(userScenarioId).name;
  }

  taPaymentModelName(taPaymentModelId: DemoTaPaymentModelId): string {
    return getDemoTaPaymentModel(taPaymentModelId).name;
  }

  tasScenarioName(tasScenarioId: DemoTasScenarioId): string {
    return getDemoTasScenarioOption(tasScenarioId).name;
  }

  debtLevelName(debtLevelId: DemoDebtLevelId): string {
    return getDemoDebtLevel(debtLevelId).name;
  }

  /**
   * Whether the selected benefit set includes Tuition Assistance. The TA payment
   * model is a TA-only axis (assistance manner + direct-pay settings), so the
   * picker only offers it when TA is among the selected products.
   */
  isTaSelected(productIds: DemoProductId[]): boolean {
    return selectionHasTuitionAssistance(productIds);
  }

  /**
   * Whether the selected persona's employer is PSLF-eligible (non-profit or
   * government). For-profit personas can't qualify, so the picker disables the
   * PSLF product for them (and personaMaskedFeatures masks it in the session).
   */
  isPslfEligibleClient(clientId: DemoClientId): boolean {
    return isPslfEligibleClient(clientId);
  }

  /** Apply a demo session (persists the selection and reloads into demo mode). */
  apply(selection: DemoSessionSelection): void {
    applyDemoSession(selection);
  }
}
